import { Provider } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/provider.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { SwitchField, useAlert, useControlledField } from '@frontend/design-system';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';

type VisibilityToggleProps = {
  value: boolean;
  disabled?: boolean;
  indeterminate?: boolean;
  showOnCalendarToggle?: boolean; // flag to check whether toggle button is for "Show on Calendar" (true) or "Online Scheduling" (false)
  providerDetails?: Provider;
  trackingId: string;
};

export const VisibilityToggle = ({
  value,
  indeterminate = false,
  disabled = false,
  showOnCalendarToggle = false,
  providerDetails,
  trackingId,
}: VisibilityToggleProps) => {
  const alert = useAlert();
  const { t } = useTranslation('schedule-settings');

  const { isMultiLocation } = useSchedulingLocationInfo();
  const updateProviderMutation = SchedulerV3.Mutations.useUpdateProviderMutation({ optimisticUpdate: true });

  const updateProviderDetails = (fieldValue: boolean) => {
    let toggleProps;

    if (showOnCalendarToggle) {
      toggleProps = {
        hidden: !fieldValue,
        profile: { ...providerDetails?.profile },
      };
    } else {
      toggleProps = {
        ...(fieldValue && { hidden: !fieldValue }),
        profile: { ...providerDetails?.profile, sobs: fieldValue },
      };
    }

    updateProviderMutation
      .mutateAsync({
        id: providerDetails?.id || '',
        firstName: providerDetails?.firstName || '',
        lastName: providerDetails?.lastName || '',
        childLocationId: isMultiLocation ? providerDetails?.locationId : undefined,
        hidden: providerDetails?.hidden,
        ...toggleProps,
      })
      .then((res) => {
        if (res) {
          alert.success(t('Successfully updated provider details'));
        }
      })
      .catch(() => {
        alert.error(t('Failed to update provider details'));
      });
  };

  const switchFieldProps = useControlledField({
    value,
    type: 'switch',
    onChange: (fieldValue: boolean) => {
      updateProviderDetails(fieldValue);
    },
  });

  return (
    <SwitchField
      {...switchFieldProps}
      label={''}
      disabled={disabled}
      indeterminate={indeterminate}
      name={showOnCalendarToggle ? 'calendar-switch' : 'booking-switch'}
      trackingId={trackingId}
    />
  );
};
